import React from "react";
import { Column } from "react-table";
import { CaptionProp, col } from "../../../@types/react-table";
import { RenderedTable } from "../../../components/Tables";
import { Plan, AdditionalSupplementalBenefitCoverage } from "../../../@types";
import { useTableConfig, useTranslate } from "../../../helpers";
import { getAdditionalBenefit } from "../../../helpers/planHelpers";
import { MMPBenefitData, MoreBenefitData, SpecialBenefitData } from "./data";

/**
 * SubSectionTable represents a sub-section table in the Extra Benefits Section
 * @param data of data to display in the table
 * @param caption the section caption
 * @param hasLimits true if the table should have a "limits apply" column, otherwise false.
 * @returns JSX.Element
 */
export const SubSectionTable: React.FunctionComponent<{
  data: Record<string, unknown>[];
  caption: CaptionProp;
  hasLimits: boolean;
}> = ({ data, caption, hasLimits }) => {
  let tableClassName = "two-column-table";

  const cols: Array<Column<col>> = [
    {
      accessor: "label",
    },
    {
      accessor: "value",
    },
  ];

  if (hasLimits) {
    cols.push({
      accessor: "limitsApply",
    });
    tableClassName = "three-column-table";
  }
  const t = useTranslate();

  return (
    <div>
      <RenderedTable
        className={tableClassName}
        tableColumns={useTableConfig(cols)}
        tableData={React.useMemo(() => data, [t])}
        rowHeaders // Indicates the first column should be rendered as `th scope="row"`
        borderless // is a default for Plan Details, TBD if it should be baked in
        stackableBreakpoint="lg" // is a default for Plan Details, TBD if it should be baked in
        caption={caption}
      />
    </div>
  );
};

/**
 * convert array of MMPBenefitData[] into table-friendly data that
 * can be rendered by `<RenderedTable>`
 * @param plan the plan
 * @param suppressed indicates to suppress the benefit or not
 * @param t translation function
 * @param benefits array of MMPBenefitData[]
 * @returns Record<string, unknown>[]
 */
export const convertMMPBenefitData = (
  plan: Plan,
  suppressed: boolean,
  t: (id: string) => string,
  benefits: MMPBenefitData[]
): Record<string, unknown>[] => {
  return benefits.reduce(
    (accumulator: Record<string, unknown>[], benefit: MMPBenefitData) => {
      const detail = getAdditionalBenefit(
        plan,
        "mmp_benefits",
        benefit.category,
        benefit.benefit
      );
      if (detail) {
        const coverageKey =
          detail.coverage ===
          AdditionalSupplementalBenefitCoverage.SB_COVERAGE_SOME_COVERAGE
            ? detail.coverage + "_MMP"
            : detail.coverage;
        accumulator.push({
          label: benefit.label,
          value: suppressed
            ? t("suppressed_plan_benefit")
            : t(coverageKey.toLowerCase()),
        });
      }
      return accumulator;
    },
    []
  );
};

/**
 * convert array of MoreBenefitData[] into table-friendly data that
 * can be rendered by `<RenderedTable>`
 * @param plan the plan
 * @param suppressed indicates to suppress the benefit or not
 * @param t translation function
 * @param benefits array of MoreBenefitData[]
 * @returns Record<string, unknown>[]
 */
export const convertMoreBenefitData = (
  plan: Plan,
  suppressed: boolean,
  t: (id: string) => string,
  benefits: MoreBenefitData[]
): Record<string, unknown>[] => {
  return benefits.reduce(
    (accumulator: Record<string, unknown>[], benefit: MoreBenefitData) => {
      const detail = getAdditionalBenefit(
        plan,
        "other_benefits",
        benefit.category,
        benefit.benefit
      );
      if (detail) {
        accumulator.push({
          label: benefit.label,
          value: suppressed
            ? t("suppressed_plan_benefit")
            : t(detail.coverage.toLocaleLowerCase()),
        });
      } else if (benefit.defaultToNotCovered) {
        accumulator.push({
          label: benefit.label,
          value: suppressed
            ? t("suppressed_plan_benefit")
            : t("sb_coverage_not_covered"),
        });
      }
      return accumulator;
    },
    []
  );
};

/**
 * convert array of SpecialBenefitData[] into table-friendly data that
 * can be rendered by `<RenderedTable>`
 * @param plan the plan
 * @param suppressed indicates to suppress the benefit or not
 * @param t translation function
 * @param benefits array of SpecialBenefitData[]
 * @returns Record<string, unknown>[]
 */
export const convertSpecialBenefitData = (
  plan: Plan,
  suppressed: boolean,
  t: (id: string) => string,
  benefits: SpecialBenefitData[]
): Record<string, unknown>[] => {
  return benefits.reduce(
    (accumulator: Record<string, unknown>[], benefit: SpecialBenefitData) => {
      const detail = getAdditionalBenefit(
        plan,
        "special_benefits",
        benefit.category,
        benefit.benefit
      );
      if (detail) {
        accumulator.push({
          label: benefit.label,
          value: suppressed
            ? t("suppressed_plan_benefit")
            : t(detail.coverage.toLowerCase()),
        });
      }
      return accumulator;
    },
    []
  );
};
